<script setup lang="ts">
import TooltipContents from "@/components/TooltipContents.vue";
import { ProductPermissions } from "@/models/ProductPermissions";
import { isIOS } from "@vueuse/core";
import ToolbarButton from "./ToolbarButton.vue";

const props = withDefaults(
  defineProps<{
    enablePrint?: boolean;
    enableCopy?: boolean;
    enableEmail?: boolean;
    enableShare?: boolean;
    dropdownDownload?: boolean;
    product: string;
    showBusy?: {
      copy: boolean;
      email: boolean;
      print: boolean;
      download: boolean;
      share: boolean;
    };
    ids: {
      copy: string;
      email: string;
      print: string;
      download: string;
      downloadPDF?: string;
      downloadChordPro?: string;
      share: string;
      fullscreen: string;
    };
    maxSongUsageCheck?: (product: string) => boolean;
  }>(),
  {
    enablePrint: false,
    enableCopy: false,
    enableEmail: false,
    enableShare: false,
  },
);

const fs = useFullScreenStore();
const modals = useModalStore();
const songStore = useSongStore();
const midiStore = useMidiStore();

const supportsShare = !!navigator.share || false;

const chordProNotAuthorized = computed(
  () =>
    (songStore.song?.products.chordPro.exists &&
      !songStore.song?.products.chordPro.authorized) ||
    false,
);

const chordProExists = computed(() => songStore.song?.products.chordPro.exists);

const emit = defineEmits<{
  (e: "copy"): void;
  (e: "print"): void;
  (e: "download"): void;
  (e: "downloadChordPro"): void;
  (e: "share"): void;
  (e: "email"): void;
}>();

function onEmail() {
  if (!checkProductAuth()) {
    return;
  }
  if (props.maxSongUsageCheck && props.maxSongUsageCheck(props.product)) {
    songStore.showMaxSongUsage();
    return;
  }
  removeTooltip(props.enableEmail);
  emit("email");
}

function onDownload(product = props.product) {
  if (!songStore.data) {
    return;
  }
  if (!checkProductAuth()) {
    return;
  }

  if (product === "chordpro" && chordProNotAuthorized.value) {
    modals.showModal("ChordProInfoModal");
    return;
  }

  if (props.maxSongUsageCheck && props.maxSongUsageCheck(product)) {
    songStore.showMaxSongUsage();
    return;
  }
  removeTooltip(true);
  if (product === "chordpro") {
    emit("downloadChordPro");
  } else {
    emit("download");
  }
}

function onPrint() {
  if (!checkProductAuth()) {
    return;
  }
  if (props.maxSongUsageCheck && props.maxSongUsageCheck(props.product)) {
    songStore.showMaxSongUsage();
    return;
  }
  removeTooltip(props.enablePrint);
  emit("print");
}

async function onCopy() {
  if (!checkProductAuth()) {
    return;
  }
  if (props.maxSongUsageCheck && props.maxSongUsageCheck(props.product)) {
    songStore.showMaxSongUsage();
    return;
  }
  removeTooltip(props.enableCopy);
  emit("copy");
  showCopied.value = false;
  await nextTick();
  showCopied.value = true;
  await asyncWait(1000);
  showCopied.value = false;
}

function onShare() {
  if (!checkProductAuth()) {
    return;
  }
  if (props.maxSongUsageCheck && props.maxSongUsageCheck(props.product)) {
    songStore.showMaxSongUsage();
    return;
  }
  removeTooltip(props.enableShare);
  emit("share");
}

function removeTooltip(action: boolean) {
  setTimeout(() => {
    if (action) {
      // document.querySelectorAll(".tooltip").forEach((i) => {
      //   i.classList.remove("show");
      // });
    }
  }, 1500);
}

const productAuth = computed(() => {
  if (!songStore.song) {
    return true;
  }

  const permissions: ProductPermissions | null =
    (songStore.song.products as { [key: string]: any })[props.product] || null;

  if (!permissions) {
    return true;
  }
  return !!permissions.authorized;
});

function checkProductAuth() {
  if (!songStore.song) {
    return true;
  }

  const permissions: ProductPermissions | null =
    (songStore.song.products as { [key: string]: any })[props.product] || null;

  if (!permissions) {
    return true;
  }

  if (permissions.authorized) {
    return true;
  }

  switch (permissions.noAuthReason) {
    case NoAuthReason.NotAuthenticated:
      modals.showModal("SignInModal");
      return false;
    case NoAuthReason.LyricsRequireSsSubscription:
      modals.showModal("SSRequiredModal");
      return false;
    case NoAuthReason.RequiresSubscriptionUpgrade:
      modals.showModal("UpgradeRequiredModal");
      return false;
  }

  return true;
}

// on ios devices where browser is NOT safari, printing is not supported (See https://ccli.atlassian.net/browse/SS-606)

function toggleFullScreen(e: MouseEvent | Event) {
  if (fs.fullScreenEnabled) {
    fs.exitFullScreen();
  } else {
    fs.enterFullScreen(e);
  }
  midiStore.setFullMidiController(false);
}

const copybtn = ref<InstanceType<typeof ToolbarButton>>();
const showCopied = ref(false);
</script>

<template>
  <ToolbarButtonGroup
    :title="$t('SV.LIT_SheetMusicActions')"
    class="hidden xl:block"
  >
    <ToolbarButton
      v-if="enableCopy && songStore.showCopyButton"
      :id="ids.copy"
      ref="copybtn"
      icon="fa-copy"
      :label="$t('G.LIT_Copy')"
      :click-title="productAuth ? $t('SV.LIT_CopiedToClipboard') : undefined"
      :product="product"
      :show-busy="showBusy?.copy"
      data-content-action="copy"
      @click="onCopy"
    />
    <TooltipContents
      v-if="copybtn && showCopied"
      :label="copybtn.$el"
      placement="bottom"
      :allowed-placements="['bottom']"
      ><FAIcon class="fa-light fa-check" />
      {{ $t("SV.LIT_CopiedToClipboard") }}</TooltipContents
    >
    <ToolbarButton
      v-if="enablePrint"
      :id="ids.print"
      icon="fa-print"
      :label="$t('G.LIT_Print')"
      :product="product"
      :show-busy="showBusy?.print"
      data-content-action="print"
      @click="onPrint"
    />
    <ToolbarButton
      v-if="!dropdownDownload"
      :id="ids.download"
      icon="fa-download"
      :label="$t('S.LIT_Download')"
      :product="product"
      :show-busy="showBusy?.download"
      data-content-action="download"
      :aria-label="$t('S.LIT_Download')"
      @click="onDownload()"
    />

    <ToolbarButtonDropdown
      v-if="dropdownDownload"
      :label="$t('S.LIT_Download')"
      placement="bottom-end"
      icon="fa-download"
      :show-busy="showBusy?.download"
    >
      <ToolbarButtonDropdownItem
        :id="ids.downloadPDF"
        data-content-action="download"
        icon="fa-file-pdf"
        @click="onDownload('chords')"
      >
        {{ $t("G.LIT_PDF") }}
      </ToolbarButtonDropdownItem>

      <ToolbarButtonDropdownItem
        :id="ids.downloadChordPro"
        data-content-action="downloadchordpro"
        icon="fa-file-lines"
        :disabled="!chordProExists"
        @click="onDownload('chordpro')"
      >
        <div>{{ $t("S.LIT_Download") }} {{ $t("G.ContentFacetChordPro") }}</div>
        <div
          v-if="!chordProExists"
          class="small"
        >
          {{ $t("S.ChordProUnavailableToolTip") }}
        </div>
        <template #end>
          <button
            type="button"
            :title="$t('SV.LIT_InformationAboutChordPro')"
            @click="modals.showModal('ChordProInfoModal')"
          >
            <FAIcon class="fa-light fa-circle-question fa-fw text-primary" />
          </button>
        </template>
      </ToolbarButtonDropdownItem>
    </ToolbarButtonDropdown>
    <ToolbarButton
      v-if="enableShare && supportsShare"
      :id="ids.share"
      icon="fa-share-nodes"
      :label="$t('S.LIT_Share')"
      :product="product"
      :show-busy="showBusy?.share"
      data-content-action="share"
      @click="onShare"
    />
    <ToolbarButton
      v-if="enableEmail"
      :id="ids.email"
      icon="fa-envelope"
      :label="$t('G.LIT_Email')"
      :product="product"
      :show-busy="showBusy?.email"
      data-content-action="email"
      @click="onEmail"
    />
    <ToolbarButton
      v-if="!isIOS"
      :id="ids.fullscreen"
      :icon="fs.fullScreenEnabled ? 'fa-compress' : 'fa-expand'"
      :label="
        fs.fullScreenEnabled
          ? $t('S.LIT_ExitFullScreenTC')
          : $t('S.LIT_FullScreenTC')
      "
      :product="product"
      :active="fs.fullScreenEnabled"
      :show-title="fs.fullScreenEnabled"
      @click="toggleFullScreen"
    />
  </ToolbarButtonGroup>
  <ToolbarButtonGroup
    :title="$t('SV.LIT_SheetMusicActions')"
    class="block xl:hidden"
  >
    <ToolbarButtonDropdown
      :label="$t('SV.LIT_Export')"
      placement="bottom-end"
      :icon="
        showBusy?.print ||
        showBusy?.download ||
        showBusy?.email ||
        showBusy?.share ||
        showBusy?.copy
          ? 'fa-spin fa-spinner-third'
          : 'fa-arrow-up-from-bracket'
      "
    >
      <ToolbarButtonDropdownItem
        v-if="enableCopy && songStore.showCopyButton"
        :id="ids.copy"
        icon="fa-copy"
        :click-title="productAuth ? $t('SV.LIT_CopiedToClipboard') : undefined"
        :product="product"
        :show-busy="showBusy?.copy"
        data-content-action="copy"
        @click="onCopy"
        >{{ $t("G.LIT_Copy") }}</ToolbarButtonDropdownItem
      >
      <ToolbarButtonDropdownItem
        v-if="enablePrint"
        :id="ids.print"
        icon="fa-print"
        :show-busy="showBusy?.print"
        @click="onPrint"
        >{{ $t("G.LIT_Print") }}</ToolbarButtonDropdownItem
      >

      <ToolbarButtonDropdownItem
        v-if="!dropdownDownload"
        :id="ids.download"
        icon="fa-download"
        :label="$t('S.LIT_Download')"
        :product="product"
        :show-busy="showBusy?.download"
        data-content-action="download"
        :aria-label="$t('S.LIT_Download')"
        @click="onDownload()"
        >{{ $t("S.LIT_Download") }}</ToolbarButtonDropdownItem
      >

      <ToolbarButtonDropdownItem
        v-if="dropdownDownload"
        :id="ids.downloadPDF"
        data-content-action="download"
        icon="fa-file-pdf"
        @click="onDownload('chords')"
      >
        {{ $t("S.LIT_Download") }} {{ $t("G.LIT_PDF") }}
      </ToolbarButtonDropdownItem>

      <ToolbarButtonDropdownItem
        v-if="dropdownDownload"
        :id="ids.downloadChordPro"
        data-content-action="downloadchordpro"
        icon="fa-file-lines"
        :disabled="!chordProExists"
        @click="onDownload('chordpro')"
      >
        {{ $t("S.LIT_Download") }} {{ $t("G.ContentFacetChordPro") }}
        <div
          v-if="!chordProExists"
          class="small"
        >
          {{ $t("S.ChordProUnavailableToolTip") }}
        </div>
        <template #end>
          <button
            type="button"
            :title="$t('SV.LIT_InformationAboutChordPro')"
            @click="modals.showModal('ChordProInfoModal')"
          >
            <FAIcon class="fa-light fa-circle-question fa-fw text-primary" />
          </button>
        </template>
      </ToolbarButtonDropdownItem>

      <ToolbarButtonDropdownItem
        v-if="enableShare && supportsShare"
        :id="ids.share"
        icon="fa-share-nodes"
        :product="product"
        :show-busy="showBusy?.share"
        data-content-action="share"
        @click="onShare"
        >{{ $t("S.LIT_Share") }}</ToolbarButtonDropdownItem
      >
      <ToolbarButtonDropdownItem
        v-if="enableEmail"
        :id="ids.email"
        icon="fa-envelope"
        :product="product"
        :show-busy="showBusy?.email"
        data-content-action="email"
        @click="onEmail"
      >
        {{ $t("G.LIT_Email") }}
      </ToolbarButtonDropdownItem>
    </ToolbarButtonDropdown>
    <ToolbarButton
      v-if="!isIOS"
      :id="ids.fullscreen"
      :icon="fs.fullScreenEnabled ? 'fa-compress' : 'fa-expand'"
      :label="
        fs.fullScreenEnabled
          ? $t('S.LIT_ExitFullScreenTC')
          : $t('S.LIT_FullScreenTC')
      "
      :product="product"
      :active="fs.fullScreenEnabled"
      :show-title="fs.fullScreenEnabled"
      @click="toggleFullScreen"
    />
  </ToolbarButtonGroup>
</template>

<style scoped lang="postcss"></style>
